/**
 * IntakyTvPage — TV/dashboard mode for office displays.
 *
 * Flighty TV layout:
 *   - Floating dark rounded header card with firm name + branding
 *   - Colored ticker banner for significant events (signed, rejected)
 *   - Left: card-style activity feed over the map
 *   - Right: full-bleed grayscale Apple Map
 *   - Cha-ching on signed
 */
import {
  ArrowPathIcon,
  BellAlertIcon,
  ChatBubbleLeftIcon,
  CheckBadgeIcon,
  ClockIcon,
  DocumentArrowUpIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  ShieldCheckIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  INTAKY_FONT,
} from '../components/intaky/intaky-tokens';
import type { IntakyMapIntake, MapTooltip } from '../components/intaky/IntakyMap';
import { IntakyMap } from '../components/intaky/IntakyMap';

const API_BASE = import.meta.env.VITE_APP_DJANGO_BACKEND_HOST as string;

interface Activity {
  id: string;
  intake_id: string;
  activity_type: string;
  headline: string;
  detail: string;
  occurred_at: string;
  metadata: Record<string, string>;
  intake_snapshot?: {
    status: string;
    client_display: string;
    case_type: string;
    referral_source: string;
    city: string;
    state: string;
    location_precision: string;
    rejection_reason?: string;
  } | null;
}

interface OverviewResponse {
  firm_name: string;
  total_active: number;
  intakes: IntakyMapIntake[];
  activities: Activity[];
}

function isSignificant(act: Activity): boolean {
  if (act.activity_type === 'loe_signed') return true;
  if (act.activity_type === 'status_changed') {
    const ns = act.metadata?.new_status;
    return ns === 'signed' || ns === 'rejected' || ns === 'archived' || ns === 'chase';
  }
  return false;
}

function tickerColor(act: Activity): string {
  const ns = act.metadata?.new_status ?? '';
  if (ns === 'signed' || act.activity_type === 'loe_signed') return '#16A34A';
  if (ns === 'rejected' || ns === 'archived') return '#DC2626';
  if (ns === 'chase') return '#D97706';
  return '#F59E0B';
}

const LABELS: Record<string, string> = {
  status_changed: 'Status Change',
  chase_note_added: 'Note Added',
  loe_sent: 'LOE Sent',
  loe_signed: 'Retainer Signed',
  callback_scheduled: 'Callback',
  callback_cancelled: 'Callback Cancelled',
  chase_sms_sent: 'SMS Sent',
  intake_created: 'New Intake',
  screening_updated: 'Screening',
  document_uploaded: 'Document',
  escalated: 'Escalated',
};

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

/** Icon for activity feed cards — matches activity_type to a Heroicon */
function activityIcon(actType: string, ns: string, size: number, color: string): React.ReactNode {
  const s = { width: size, height: size, color, flexShrink: 0 } as const;
  if (ns === 'signed' || actType === 'loe_signed') return <CheckBadgeIcon style={s} />;
  if (ns === 'rejected') return <XCircleIcon style={s} />;
  if (ns === 'archived' || ns === 'lost') return <XMarkIcon style={s} />;
  if (actType === 'loe_sent') return <DocumentArrowUpIcon style={s} />;
  if (actType === 'escalated') return <ExclamationTriangleIcon style={s} />;
  if (actType === 'intake_created') return <PlusCircleIcon style={s} />;
  if (actType === 'chase_sms_sent') return <ChatBubbleLeftIcon style={s} />;
  if (actType === 'chase_note_added') return <ChatBubbleLeftIcon style={s} />;
  if (actType === 'callback_scheduled') return <ClockIcon style={s} />;
  if (actType === 'callback_cancelled') return <XMarkIcon style={s} />;
  if (actType === 'screening_updated') return <ShieldCheckIcon style={s} />;
  if (actType === 'document_uploaded') return <DocumentIcon style={s} />;
  if (actType === 'status_changed') return <ArrowPathIcon style={s} />;
  return <BellAlertIcon style={s} />;
}

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export const IntakyTvPage: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const overviewUrl = firmId
    ? `${API_BASE}/api/intaky/${firmId}/overview/`
    : `${API_BASE}/api/intaky/all/overview/`;
  const streamUrl = firmId
    ? `${API_BASE}/api/intaky/${firmId}/stream/`
    : `${API_BASE}/api/intaky/all/stream/`;

  const [firmName, setFirmName] = useState('');
  const [intakes, setIntakes] = useState<IntakyMapIntake[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [tickerItems, setTickerItems] = useState<Activity[]>([]);
  const [tickerVersion, setTickerVersion] = useState(0);
  const [tickerIndex, setTickerIndex] = useState(0);
  const [connected, setConnected] = useState(false);
  const [mapTooltips] = useState<MapTooltip[]>([]);
  // Tracks ALL intake names including closed ones — so ticker/feed can show names
  const nameMapRef = useRef<Record<string, { name: string; state: string; caseType: string; rejectionReason: string }>>({});
  const seenRef = useRef(new Set<string>());
  // Tick every 5s so stale ticker items get filtered out
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(iv);
  }, []);
  // Filter ticker to only show events from the last minute
  const TICKER_WINDOW_MS = 60 * 1000;
  const recentTickerItems = tickerItems.filter(
    (act) => now - new Date(act.occurred_at).getTime() < TICKER_WINDOW_MS,
  );
  const showTicker = recentTickerItems.length > 0;
  const [showConfetti, setShowConfetti] = useState(false);
  const confettiTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const esRef = useRef<EventSource | null>(null);
  const chachingRef = useRef<HTMLAudioElement | null>(null);

  // Unlock audio on first user interaction (browser autoplay policy)
  useEffect(() => {
    const a = new Audio('/audio/cha-ching.mp3');
    a.preload = 'auto';
    a.volume = 0.8;
    chachingRef.current = a;
    const unlock = () => {
      // Play a silent moment to unlock the audio context
      a.volume = 0;
      a.play().then(() => { a.pause(); a.currentTime = 0; a.volume = 0.8; }).catch(() => {});
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
      document.removeEventListener('touchstart', unlock);
    };
    document.addEventListener('click', unlock);
    document.addEventListener('keydown', unlock);
    document.addEventListener('touchstart', unlock);
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  useEffect(() => {
    if (tickerItems.length <= 1) return undefined;
    const iv = setInterval(() => setTickerIndex((p) => (p + 1) % tickerItems.length), 5000);
    return () => clearInterval(iv);
  }, [tickerItems.length]);

  useEffect(() => {
    fetch(overviewUrl)
      .then((r) => r.json() as Promise<OverviewResponse>)
      .then((data) => {
        setFirmName(data.firm_name);
        // Build name map from ALL intakes (including closed) before filtering
        data.intakes.forEach((i: IntakyMapIntake) => {
          nameMapRef.current[i.id] = { name: i.client_display, state: i.state, caseType: i.case_type || '', rejectionReason: i.rejection_reason || '' };
        });
        const CLOSED = new Set(['rejected', 'archived', 'lost']);
        setIntakes(data.intakes.filter((i: IntakyMapIntake) => !CLOSED.has(i.status)));
        setActivities(data.activities.slice(0, 50));
        data.activities.forEach((a: Activity) => seenRef.current.add(a.id));
        setTickerItems(data.activities.filter(isSignificant).slice(0, 10));
      })
      .catch(() => {});
  }, [overviewUrl]);

  const connectSSE = useCallback(() => {
    if (esRef.current) return;
    const es = new EventSource(streamUrl, { withCredentials: true });
    esRef.current = es;
    es.onopen = () => setConnected(true);
    es.onmessage = (e) => {
      try {
        const act = JSON.parse(e.data) as Activity;
        if (seenRef.current.has(act.id)) return;
        seenRef.current.add(act.id);
        const snap = act.intake_snapshot;
        // Always update name map from snapshot
        if (snap) {
          nameMapRef.current[act.intake_id] = { name: snap.client_display, state: snap.state, caseType: snap.case_type || '', rejectionReason: snap.rejection_reason || '' };
        }

        // 🎉 Celebrate signing — cha-ching + confetti
        const isSigning = act.activity_type === 'loe_signed'
          || act.metadata?.new_status === 'signed';
        if (isSigning) {
          const audio = chachingRef.current;
          if (audio) {
            audio.currentTime = 0;
            audio.play().catch((err) => console.warn('[TV] Audio play blocked:', err));
          }
          setShowConfetti(true);
          if (confettiTimeoutRef.current) clearTimeout(confettiTimeoutRef.current);
          confettiTimeoutRef.current = setTimeout(() => setShowConfetti(false), 5000);
        }

        setIntakes((prev) => {
          const exists = prev.some((i) => i.id === act.intake_id);
          if (!exists && snap) {
            return [{ id: act.intake_id, client_display: snap.client_display, status: snap.status, state: snap.state, city: snap.city, location_precision: snap.location_precision as 'city' | 'state' | 'none', case_type: snap.case_type, referral_source: snap.referral_source, last_activity_headline: act.headline, last_activity_detail: act.detail || '', last_activity_at: act.occurred_at }, ...prev];
          }
          return prev.map((intake) => {
            if (intake.id !== act.intake_id) return intake;
            return { ...intake, last_activity_headline: act.headline, last_activity_detail: act.detail || '', last_activity_at: act.occurred_at, ...(snap && { status: snap.status, client_display: snap.client_display, case_type: snap.case_type, referral_source: snap.referral_source, city: snap.city, state: snap.state, location_precision: snap.location_precision as 'city' | 'state' | 'none' }) };
          });
        });
        setActivities((prev) => [act, ...prev].slice(0, 100));
        if (isSignificant(act)) {
          setTickerItems((prev) => [act, ...prev].slice(0, 10));
          setTickerVersion((v) => v + 1);
        }
      } catch { /* */ }
    };
    es.onerror = () => { setConnected(false); esRef.current?.close(); esRef.current = null; setTimeout(connectSSE, 3000); };
  }, [streamUrl]);

  useEffect(() => { connectSSE(); return () => { esRef.current?.close(); esRef.current = null; }; }, [connectSSE]);

  const currentTicker = tickerItems[tickerIndex];

  return (
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', background: '#F7F7F7', fontFamily: INTAKY_FONT.family, position: 'relative' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes tv-slide { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes tv-row-in { from { opacity: 0; transform: translateY(20px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes tv-marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes tv-ticker-appear { from { height: 0; opacity: 0; } to { height: 48px; opacity: 1; } }
        @keyframes intaky-dot-pop { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.15); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
        @keyframes intaky-ping { 0% { transform: scale(1); opacity: 0.5; } 80% { transform: scale(2.6); opacity: 0; } 100% { transform: scale(2.6); opacity: 0; } }
        @keyframes tv-confetti-fall {
          0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          85% { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
        @keyframes tv-confetti-sway {
          0%, 100% { margin-left: 0; }
          25% { margin-left: 20px; }
          75% { margin-left: -20px; }
        }
        @keyframes tv-pulse-dot {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.6; }
        }
        .tv-feed-scroll::-webkit-scrollbar { display: none; }
        .tv-feed-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        /* Greyscale only the map tiles (canvas), not the annotation overlays */
        .tv-map-grey canvas { filter: saturate(0) contrast(1) brightness(1); }
        /* Left-edge mask: fades map from transparent (left 50%) to opaque (right) */
        .tv-map-mask {
          -webkit-mask-image: linear-gradient(to right, transparent 0%, transparent 35%, black 65%);
          mask-image: linear-gradient(to right, transparent 0%, transparent 35%, black 65%);
        }
      `}</style>

      {/* ── Full-bleed greyscale map with left edge mask ── */}
      <div style={{ position: 'absolute', inset: 0 }} className="tv-map-grey tv-map-mask">
        <IntakyMap
          intakes={intakes}
          tooltips={mapTooltips}
          initialCenter={[38.0, -80.0]}
          initialDistance={8_000_000}
          mapPadding={{ top: 100, right: 40, bottom: 40, left: 700 }}
          autoFit
        />
      </div>

      {/* ── Floating header card ── */}
      <div style={{ position: 'fixed', top: 32, left: 40, right: 40, zIndex: 10 }}>
        <div style={{ background: 'linear-gradient(to bottom, #27282E, #0D0C0F)', borderRadius: 20, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
          {/* Main header row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 40px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
              <button
                type="button"
                onClick={() => navigate(firmId ? `/external/intake/${firmId}` : '/external/intake/all')}
                style={{ fontSize: 42, fontWeight: 700, color: 'white', letterSpacing: '-0.02em', lineHeight: 1, cursor: 'pointer', background: 'none', border: 'none', padding: 0, fontFamily: 'inherit' }}
              >
                {firmName || 'All Firms'}
              </button>
              <span style={{ fontSize: 22, fontWeight: 600, color: 'rgba(255,255,255,0.45)' }}>
                {new Date().toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 20, fontWeight: 600, color: connected ? '#4ADE80' : '#64748B' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: connected ? '#22C55E' : '#64748B' }} />
                Live
              </div>
              <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: '0.04em' }}>
                <span style={{ color: 'rgba(255,255,255,0.7)' }}>FINCHLEGAL</span>
                <span style={{ color: 'rgba(255,255,255,0.5)' }}>.COM</span>
              </span>
            </div>
          </div>

          {/* Ticker banner — Flighty-style horizontal scrolling marquee */}
          {showTicker && (
            <div style={{ height: 48, background: tickerColor(recentTickerItems[0]), display: 'flex', alignItems: 'center', gap: 0, overflow: 'hidden', animation: 'tv-ticker-appear 0.3s ease-out' }}>
              {/* Live indicator + label */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px 0 40px', flexShrink: 0 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: 'white', animation: 'tv-pulse-dot 1.5s ease-in-out infinite' }} />
                </div>
                <span style={{ fontSize: 20, fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>Major Events</span>
                <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.25)', marginLeft: 8 }} />
              </div>
              {/* Scrolling ticker content */}
              <div key={tickerVersion} style={{ flex: 1, overflow: 'hidden', maskImage: 'linear-gradient(to right, transparent 0%, black 2%, black 98%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 2%, black 98%, transparent 100%)' }}>
                <div style={{ display: 'inline-flex', gap: 80, alignItems: 'center', animation: 'tv-marquee 30s linear infinite', whiteSpace: 'nowrap' }}>
                  {/* Repeat enough to fill screen, then duplicate that block for seamless loop */}
                  {(() => {
                    const fillCount = Math.max(2, Math.ceil(8 / recentTickerItems.length));
                    const oneSet = Array.from({ length: fillCount }).flatMap(() => recentTickerItems);
                    return [...oneSet, ...oneSet];
                  })().map((act, idx) => {
                    const info = nameMapRef.current[act.intake_id];
                    const client = info?.name || intakes.find((i) => i.id === act.intake_id)?.client_display || 'A lead';
                    const stateAbbr = info?.state || '';
                    const stateName = STATE_NAMES[stateAbbr] || stateAbbr;
                    const caseType = info?.caseType || '';
                    const ns = act.metadata?.new_status ?? '';
                    const icoStyle = { width: 20, height: 20, flexShrink: 0 } as const;

                    // Icon
                    let icon: React.ReactNode = <ArrowPathIcon style={icoStyle} />;
                    if (ns === 'signed' || act.activity_type === 'loe_signed') icon = <CheckBadgeIcon style={icoStyle} />;
                    else if (ns === 'rejected' || ns === 'archived') icon = <XCircleIcon style={icoStyle} />;
                    else if (ns === 'chase') icon = <BellAlertIcon style={icoStyle} />;
                    else if (act.activity_type === 'loe_sent') icon = <DocumentArrowUpIcon style={icoStyle} />;
                    else if (act.activity_type === 'escalated') icon = <ExclamationTriangleIcon style={icoStyle} />;

                    // Rich description
                    let desc = '';
                    if (ns === 'signed' || act.activity_type === 'loe_signed') {
                      const parts: string[] = [];
                      if (caseType) parts.push(caseType);
                      if (stateName) parts.push(`from ${stateName}`);
                      desc = parts.length > 0 ? `${parts.join(', ')} — just signed` : 'just signed';
                    } else if (ns === 'rejected') {
                      const reason = act.detail || act.intake_snapshot?.rejection_reason || info?.rejectionReason || act.metadata?.reason || '';
                      const parts: string[] = [];
                      if (stateName) parts.push(`from ${stateName}`);
                      if (reason) parts.push(`due to ${reason}`);
                      desc = parts.length > 0 ? `rejected ${parts.join(' ')}` : 'rejected';
                    } else if (ns === 'chase') {
                      const parts: string[] = [];
                      if (caseType) parts.push(caseType);
                      if (stateName) parts.push(`from ${stateName}`);
                      desc = parts.length > 0 ? `${parts.join(', ')} — actively following up` : 'actively following up';
                    } else if (ns === 'archived') {
                      desc = 'archived';
                    } else {
                      desc = act.headline.replace(/\bAuto\s+/gi, '');
                    }

                    return (
                      <span key={`${act.id}-${idx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 18, fontWeight: 500, color: 'white', flexShrink: 0 }}>
                        {icon}
                        <strong style={{ fontWeight: 700 }}>{client}</strong>
                        {` ${desc}`}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Left column: Flighty-style stat cards + activity feed ── */}
      <div className="tv-feed-scroll" style={{ position: 'absolute', top: showTicker ? 190 : 150, left: 0, bottom: 0, width: 680, maxWidth: '50vw', zIndex: 2, overflow: 'auto', padding: '24px 40px 80px', transition: 'top 0.3s ease-in-out' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* ── Activity feed cards ── */}
          {activities.map((act, idx) => {
            const info = nameMapRef.current[act.intake_id];
            const client = info?.name || intakes.find((i) => i.id === act.intake_id)?.client_display || '';
            const stateAbbr = info?.state || act.intake_snapshot?.state || '';
            const headline = act.headline.replace(/\bAuto\s+/gi, '');
            const detail = (act.detail || '').replace(/\bAuto\s+/gi, '');
            // const tier: IntakyStatusTier = act.intake_snapshot ? statusToTier(act.intake_snapshot.status) : 'new';
            // const config = INTAKY_TIERS[tier];
            const isNew = idx === 0;
            const ns = act.metadata?.new_status ?? '';
            const isSigned = ns === 'signed' || act.activity_type === 'loe_signed';

            // For rejected, pull reason from activity detail, snapshot, or intake data
            const isRejected = ns === 'rejected' || act.headline.toLowerCase().includes('declined');
            const rejectionReason = isRejected
              ? (act.detail || act.intake_snapshot?.rejection_reason || info?.rejectionReason || '').replace(/\bAuto\s+/gi, '').trim()
              : '';

            // Combine headline + detail for rich display
            let displayText = detail
              ? `${headline}: ${detail}`
              : headline;

            // Override for rejected with reason
            if (isRejected && rejectionReason) {
              displayText = `${headline}: ${rejectionReason}`;
            }

            return (
              <div
                key={act.id}
                style={{
                  background: '#f0f0f0',
                  borderRadius: 24,
                  padding: '24px 32px',
                  animation: isNew ? 'tv-row-in 0.5s cubic-bezier(0.34,1.56,0.64,1)' : undefined,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {activityIcon(act.activity_type, ns, 22, isSigned ? '#22C55E' : '#94A3B8')}
                    <span style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.01em' }}>
                      {client || act.intake_id.slice(0, 8)}
                    </span>
                    {stateAbbr && (
                      <span style={{ fontSize: 16, fontWeight: 600, color: '#94A3B8' }}>
                        {stateAbbr}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#94A3B8', flexShrink: 0 }}>
                    {timeAgo(act.occurred_at)}
                  </span>
                </div>
                <div style={{ fontSize: 20, fontWeight: 600, color: isSigned ? '#16A34A' : '#1E293B', marginLeft: 34, lineHeight: 1.5 }}>
                  {isSigned ? `🎉 ${displayText}` : displayText}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Confetti celebration overlay ── */}
      {showConfetti && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, pointerEvents: 'none', overflow: 'hidden' }}>
          {Array.from({ length: 60 }).map((_, i) => {
            const colors = ['#22C55E', '#4ADE80', '#16A34A', '#FFD700', '#FDE68A', '#FFFFFF', '#86EFAC'];
            const color = colors[i % colors.length];
            const left = Math.random() * 100;
            const delay = Math.random() * 1.5;
            const duration = 2.5 + Math.random() * 2;
            const size = 6 + Math.random() * 10;
            const isCircle = i % 3 === 0;
            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: `${left}%`,
                  top: -20,
                  width: size,
                  height: isCircle ? size : size * 2.5,
                  borderRadius: isCircle ? '50%' : 2,
                  background: color,
                  animation: `tv-confetti-fall ${duration}s ease-in ${delay}s forwards, tv-confetti-sway ${1 + Math.random()}s ease-in-out ${delay}s infinite`,
                  opacity: 0,
                }}
              />
            );
          })}
        </div>
      )}

      {/* ── Finch logo badge (Flighty-style rotated corner badge) ── */}
      <div
        style={{
          position: 'fixed',
          top: 6,
          right: 4,
          width: 64,
          height: 64,
          borderRadius: 16,
          background: 'linear-gradient(145deg, #3A3A3A, #1A1A1A)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: 'rotate(10deg)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 0 rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.08)',
          zIndex: 20,
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="28" viewBox="0 0 31 24" fill="none">
          <path fillRule="evenodd" clipRule="evenodd" d="M17.4232 5.41484C16.6753 4.3061 16.6692 4.31217 15.7284 5.25292L15.7284 5.25293L0.676797 20.1894C-0.24242 21.1086 -0.24237 21.1087 0.781835 21.8377L0.797857 21.8491C1.06602 22.04 1.3416 22.2189 1.62386 22.3853L8.56262 15.4466L8.77013 16.5808L2.49905 22.8518C2.84577 23.0179 3.20066 23.1666 3.56253 23.2974L8.56262 18.2973L8.77013 19.4315L4.5864 23.6153C6.19334 24.0345 7.90833 24.1176 9.63516 23.8326C11.0879 23.5897 12.4599 23.0634 13.7108 22.2943C19.8042 18.6108 21.055 10.7985 17.4232 5.41484Z" fill="white" />
          <path d="M30.3223 6.39559C29.7574 5.82891 29.7574 5.66698 29.717 5.50507C29.2731 2.34776 26.5292 0 23.3413 0C21.6867 0 20.1534 0.607178 18.9428 1.74057L18.055 2.63109C17.1425 3.50242 17.3853 3.18751 17.9743 3.96689C21.7674 8.98622 21.6062 16.1105 17.4497 20.9274C22.2762 22.0709 27.8608 16.4747 27.8608 10.8077C27.8608 9.87674 27.7397 8.94574 27.4976 8.05522C27.4169 7.81235 27.4976 7.56947 27.6187 7.36708C27.7801 7.16469 27.9819 7.04326 28.2643 7.04326H30.0802C30.914 7.04326 30.9131 6.98814 30.3223 6.39559Z" fill="white" />
        </svg>
      </div>
    </div>
  );
};
